import React, { useEffect, useState } from 'react';
import { RxCross2 } from 'react-icons/rx';

interface ApplicationApprovalModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: (comments: string) => void;
    isLoading?: boolean;
    patientName: string;
}

const ApplicationApprovalModal: React.FC<ApplicationApprovalModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    isLoading = false,
    patientName,
}) => {
    const [comments, setComments] = useState('');

    useEffect(() => {
        const keyHandler = ({ keyCode }: KeyboardEvent) => {
            if (keyCode === 27 && !isLoading) onClose(); // ESC key
        };
        if (isOpen) {
            document.addEventListener('keydown', keyHandler);
        }
        return () => document.removeEventListener('keydown', keyHandler);
    }, [isOpen, onClose, isLoading]);

    const handleConfirm = () => {
        onConfirm(comments);
    };

    const handleClose = () => {
        setComments(''); // Reset comments when closing
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            className='fixed left-0 top-0 z-999999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5'
            onClick={() => { if (!isLoading) handleClose(); }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className='relative w-full max-w-xl rounded-lg bg-white shadow-lg'
            >
                <button
                    className='absolute right-4 top-4 text-gray-600 hover:text-gray-900'
                    onClick={handleClose}
                    disabled={isLoading}
                >
                    <RxCross2 />
                </button>
                <div className='p-8'>
                    <div className='mb-6'>
                        <h2 className='mb-6 text-[22px] font-bold text-black'>
                            Approve Campaign Application
                        </h2>
                        
                        <p className='mb-4 text-gray-700'>
                            You are about to approve the campaign application for{' '}
                            <span className='font-semibold'>{patientName}</span>.
                        </p>
                        
                        <p className='mb-4 text-gray-700'>
                            This will change the campaign status to{' '}
                            <span className='inline-flex rounded-full bg-primary px-2 py-1 text-sm font-medium text-black'>
                                Open
                            </span>
                            , publish the campaign, and make it ready to receive donations.
                        </p>
                        
                        <p className='mb-6 text-gray-700'>
                            Please review all application sections to ensure they are complete before approving.
                        </p>
                        
                        <div className='mb-6'>
                            <label htmlFor='comments' className='mb-2 block font-medium text-black'>
                                Comments (Optional)
                            </label>
                            <textarea
                                id='comments'
                                value={comments}
                                onChange={(e) => setComments(e.target.value)}
                                placeholder='Add any comments about this approval...'
                                disabled={isLoading}
                                className='w-full rounded-lg border border-gray-300 p-3 text-gray-700 placeholder-gray-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:bg-gray-100'
                                rows={6}
                            />
                        </div>
                    </div>

                    <div className='flex justify-start space-x-4'>
                        <button
                            onClick={handleClose}
                            disabled={isLoading}
                            className='rounded-full border-[2px] border-gray-300 bg-white px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-black hover:bg-black hover:text-white disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            CANCEL
                        </button>
                        <button
                            onClick={handleConfirm}
                            disabled={isLoading}
                            className='rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-black transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8] disabled:cursor-not-allowed disabled:opacity-50'
                        >
                            {isLoading ? 'APPROVING...' : 'APPROVE'}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplicationApprovalModal;

